import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';

import ApiClient from '@lib/apiClient';

import { BASE_AI_URL } from '@/constants/constant';

import CommonImage from '@/components/CommonImage';
import Textarea from '@/components/Textarea';
import { getUserInfo } from '@/utils/Util';
import AiLoader from '@/components/AiLoader';

const frequentQuestions = [
	'Total PO amount and total PO quantity for the current year so far?',
	'Highest PO amount this year along with the corresponding order information?',
	'How many orders placed this year?',
	'Show total completed orders by brand as of today?',
	'Top 5 brands by PO value for the current year?'
];

const OrderReportingSideBar = ({ onClose }) => {
	const [question, setQuestion] = useState('');
	const [addQuestion, setAddQuestion] = useState(false);
	const [answers, setAnswers] = useState([]);
	const [showLoader, setShowLoader] = useState(false);
	const [conversationId, setConversationId] = useState(null);
	const qaHistoryRef = useRef(null);

	const handleSearch = async (e, suggestedQuestion) => {
		try {
			e.preventDefault();
			setShowLoader(true);
			setAddQuestion(false);
			setQuestion('');
			setAnswers((prev) => [...prev, { role: 'user', content: suggestedQuestion || question }]);
			setTimeout(() => {
				if (qaHistoryRef.current) {
					qaHistoryRef.current.scrollTop = qaHistoryRef.current.scrollHeight;
				}
			}, 0);

			const userInfo = getUserInfo();
			let requestBody = {
				userQuery: suggestedQuestion || question,
				userId: userInfo?.id,
				managementLevel: userInfo?.managementLevel || '',
				primaryUserType: userInfo?.primaryUserType,
				isManagement: userInfo?.isManagement || false
			};
			let params = '';
			if (conversationId) {
				params = `?conversationId=${conversationId}`;
			}
			const { data } = await ApiClient.POST(
				`${BASE_AI_URL}/orders-reporting/chat`,
				requestBody,
				params,
				true
			);
			setConversationId(data?.conversationId || null);
			setAnswers((prev) => [...prev, { role: 'assistant', content: data?.responseMessage || '' }]);
			setShowLoader(false);
		} catch {
			setAnswers((prev) => [
				...prev,
				{
					role: 'assistant',
					content:
						'Sorry, I didn’t quite understand your query. Could you please elaborate a bit more? Just a heads-up, I’m specifically trained to handle order-related queries.'
				}
			]);
			setShowLoader(false);
		}
	};

	const handleClose = () => {
		onClose();
	};

	useEffect(() => {
		if (qaHistoryRef.current) {
			qaHistoryRef.current.scrollTop = qaHistoryRef.current.scrollHeight;
		}
	}, [answers]);

	const placeholderAndFrequentQuestions = () => {
		if (answers.length === 0) {
			return (
				<div className="bg-white border-t border-gray-200 mb-20">
					<p className="text-gray-500 mb-2 regular-14">💡 Suggested Questions:</p>
					<div className="flex flex-wrap gap-2">
						{frequentQuestions.map((q, i) => (
							<span
								key={i}
								onClick={(e) => handleSearch(e, q)}
								className="mb-1 mr-2 regular-14"
								style={{
									backgroundColor: '#f5f5f5',
									padding: '8px',
									borderRadius: '10px',
									display: 'inline-block',
									cursor: 'pointer'
								}}
							>
								{q}
							</span>
						))}
					</div>
				</div>
			);
		}
	};

	return (
		<div className="sop-sidebar ask-order-related-sidebar">
			<div className="sop-sidebar-container">
				<div className="sop-sidebar-content">
					<div className="sop-sidebar-header px-3 py-12 border-bottom-solid">
						<div className="d-flex justify-content-between">
							<p className="semibold-14 reqular-14">Ask Order Related Query</p>
							<CommonImage
								src="/icons/close.svg"
								className="cursor-pointer"
								onClick={handleClose}
							/>
						</div>
					</div>
					<div className="sop-sidebar-body scroll-y-label py-3 px-4" ref={qaHistoryRef}>
						<div className="sop-sidebar-body-header">
							<div className="searched-results mt-3">
								{!showLoader && placeholderAndFrequentQuestions()}
								<div className="qa-history">
									{answers?.map((item, idx) => (
										<div className="qa-group mb-3" key={`qa_${idx}`}>
											<div className="question-container">
												{item.role === 'user' && (
													<div className="qus-and-copy-icon position-relative">
														<p className="question regular-14 mb-3">
															{item.content}
														</p>
														<span
															className="copy-icon cursor-pointer ml-2 position-absolute right-2"
															onClick={() => {
																setQuestion(item.content);
																setAddQuestion(true);
															}}
														>
															<CommonImage
																src="/icons/edit-tool.svg"
																width={20}
																height={20}
															/>
														</span>
													</div>
												)}
											</div>
											<p className="answer regular-14 mb-1">
												{item.role === 'assistant' && (
													<Markdown>{item.content}</Markdown>
												)}
											</p>
										</div>
									))}
								</div>
							</div>
							{showLoader && <AiLoader />}
						</div>
					</div>
				</div>

				<div className="sop-sidebar-footer d-flex align-items-center flex-direction-column p-3 border-top-solid">
					<div className="w-100">
						<div className="d-flex gap-3">
							{getUserInfo()?.profilePicDocument?.docUrl && (
								<img
									src={getUserInfo()?.profilePicDocument?.docUrl}
									alt="profile"
									className="profile"
								/>
							)}
							<Textarea
								autoComplete="off"
								name="question"
								value={question}
								autoFocus
								onChange={(e) => setQuestion(e.target.value)}
								style={{
									overflow: 'hidden',
									height: `${addQuestion ? '42px' : '28px'}`
								}}
								onClick={() => setAddQuestion(true)}
								placeholder="Ask a question..."
							/>
						</div>
						{addQuestion && (
							<div className="d-flex justify-content-end align-items-center w-100 mt-3">
								<div className="more-btn option">
									<button
										className="button size32 outline mr-2 dropdown-toggle"
										type="button"
										id="menu1"
										onClick={() => {
											setQuestion('');
											setAddQuestion(false);
										}}
									>
										Cancel
									</button>
								</div>
								<button className="button size32" disabled={!question} onClick={handleSearch}>
									<span className="mobile-version-btn">Submit</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default OrderReportingSideBar;
